import { useEffect, useState } from 'react';
import { Crown, Trophy, Users } from 'lucide-react';
import { Badges } from './badges';

const MEDAL = {
  1: { num: 'text-amber-300 drop-shadow-[0_0_14px_rgba(252,211,77,0.75)]', ring: 'border-amber-300/50 bg-amber-300/[0.07]', label: 'GOLD' },
  2: { num: 'text-slate-200 drop-shadow-[0_0_12px_rgba(226,232,240,0.55)]', ring: 'border-slate-300/30 bg-white/[0.04]', label: 'SILVER' },
  3: { num: 'text-orange-300 drop-shadow-[0_0_12px_rgba(253,186,116,0.6)]', ring: 'border-orange-300/35 bg-orange-300/[0.05]', label: 'BRONZE' },
};

const Cell = ({ label, value, hot }) => (
  <div className={`w-[84px] min-h-[52px] flex flex-col justify-center text-center rounded-md px-1.5 py-1 ${hot ? 'bg-teal-400/15 border border-teal-300/40' : 'bg-white/[0.04] border border-white/10'}`}>
    <p className={`font-mono tabular-nums leading-none ${hot ? 'text-2xl font-bold text-teal-300' : 'text-base text-white/85'}`}>{value}</p>
    <p className="text-[8px] leading-tight tracking-[0.12em] text-white/50 mt-1">{label}</p>
  </div>
);

const Bar = ({ pct }) => {
  const [w, setW] = useState(0);
  useEffect(() => { const t = setTimeout(() => setW(pct), 80); return () => clearTimeout(t); }, [pct]);
  return (
    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full rounded-full bg-gradient-to-r from-teal-400 to-amber-300 shadow-[0_0_10px_rgba(45,212,191,0.6)] transition-[width] duration-1000 ease-out" style={{ width: `${pct ? Math.max(pct, 4) : 0}%` }} />
    </div>
  );
};

const Line = ({ x, lead, idx }) => {
  const m = (x.registrations || x.paid) ? MEDAL[x.rank] : null;
  return (
    <div style={{ animationDelay: `${idx * 70}ms` }}
      className={`sb-in grid grid-cols-[52px_1fr_auto] items-center gap-3 rounded-xl border px-3 ${m ? `py-3 ${m.ring}` : 'py-2 border-white/5 hover:bg-white/[0.03]'} transition-colors`}
      data-testid={m ? `podium-card-${x.rank}-${x.code}` : `leaderboard-row-${x.rank}-${x.code}`}>
      <div className="text-center">
        <p className={`font-outfit font-black tabular-nums leading-none ${m ? `text-4xl ${m.num}` : 'text-xl text-white/35'}`}>{String(x.rank).padStart(2, '0')}</p>
        {m && <p className="text-[8px] tracking-[0.25em] text-white/40 mt-1">{m.label}</p>}
      </div>
      <div className="min-w-0 space-y-1.5">
        <p className="flex items-center gap-1.5 font-semibold text-white truncate">
          {x.rank === 1 && m && <Crown className="w-4 h-4 text-amber-300 shrink-0" />}
          <span className="truncate" title={x.name}>{x.name}</span>
          {x.code !== x.name && <span className="font-mono text-[10px] text-white/50 border border-white/15 rounded px-1">{x.code}</span>}
        </p>
        {x.college && <p className="text-[11px] text-white/40 truncate">{x.college}</p>}
        <Bar pct={lead ? Math.round((100 * x.paid) / lead) : 0} />
        <Badges x={x} />
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        <Cell label="REGISTRATIONS" value={x.registrations} /><Cell label="ASSESSMENTS COMPLETED" value={x.assessments} />
        <Cell label="CONSULTATIONS BOOKED" value={x.consultations} /><Cell label="PAID CONVERSIONS" value={x.paid} hot />
      </div>
    </div>
  );
};

export const Board = ({ title, rows, testId }) => {
  const empty = !rows.some((x) => x.registrations || x.paid);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a1a40]/80 backdrop-blur-sm overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <h4 className="font-outfit font-bold tracking-wide text-white flex items-center gap-2 uppercase text-sm"><Trophy className="w-4 h-4 text-amber-300" />{title}</h4>
        <span className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] text-teal-300"><span className="w-1.5 h-1.5 rounded-full bg-teal-300 animate-pulse" />LIVE · {rows.length}</span>
      </div>
      {empty ? (
        <div className="m-4 p-10 text-center flex flex-col items-center rounded-xl border border-dashed border-white/15" data-testid={`${testId}-empty`}>
          <Users className="w-10 h-10 text-white/25 mb-2" />
          <p className="font-outfit font-semibold text-white">No registrations yet in this period</p>
          <p className="text-xs text-white/50 mt-1 max-w-xs">First person to bring in a student takes the lead. Share those referral links!</p>
        </div>
      ) : (
        <div className="p-3 space-y-2">{rows.map((x, i) => <Line key={x.code} x={x} lead={rows[0].paid} idx={i} />)}</div>
      )}
    </div>
  );
};
